import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { Wallet, Funnel, DownloadSimple, ChartPie, CalendarBlank, Check, Tag, ArrowCounterClockwise } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { format, parse } from 'date-fns';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import api from '../api';
import { useFilters } from '../context/FilterContext';

const CHART_COLORS = ['#0ea5e9', '#22c55e', '#eab308', '#f97316', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f43f5e', '#6366f1'];

const HighValue = () => {
  const [allTransactions, setAllTransactions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  // Use filter context for persistent filters
  const { highValueFilters, setHighValueFilters, resetHighValueFilters } = useFilters();
  const { selectedCategories, startDate, endDate } = highValueFilters;

  // Local UI state for pickers
  const [showStartPicker, setShowStartPicker] = useState(false);
  const [showEndPicker, setShowEndPicker] = useState(false);
  const [showCategoryPicker, setShowCategoryPicker] = useState(false);

  // Helper functions to update filters
  const setSelectedCategories = (updater) => {
    setHighValueFilters(prev => ({
      ...prev,
      selectedCategories: typeof updater === 'function' ? updater(prev.selectedCategories) : updater
    }));
  };

  const setStartDate = (date) => {
    setHighValueFilters(prev => ({ ...prev, startDate: date }));
  };

  const setEndDate = (date) => {
    setHighValueFilters(prev => ({ ...prev, endDate: date }));
  };

  useEffect(() => {
    fetchSettings();
    fetchCategories();
    fetchAllTransactions();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/settings');
      setSettings(response.data);
    } catch (error) {
      toast.error('Failed to load settings');
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get('/categories');
      // Sort alphabetically with ETC always last
      const sorted = response.data.sort((a, b) => {
        if (a.name === 'ETC') return 1;
        if (b.name === 'ETC') return -1;
        return a.name.localeCompare(b.name);
      });
      setCategories(sorted);
    } catch (error) {
      toast.error('Failed to load categories');
    }
  };

  const fetchAllTransactions = async () => {
    try {
      // Fetch all transactions for accurate high-value filtering
      const response = await api.get('/transactions', { params: { limit: 10000 } });
      setAllTransactions(response.data);
    } catch (error) {
      toast.error('Failed to load transactions');
    } finally {
      setLoading(false);
    }
  };

  const toggleCategory = (categoryName) => {
    setSelectedCategories(prev => 
      prev.includes(categoryName) 
        ? prev.filter(c => c !== categoryName)
        : [...prev, categoryName]
    );
  };

  const clearFilters = () => {
    resetHighValueFilters();
  };

  // Only apply date filter when BOTH dates are specified
  const hasDateFilter = startDate && endDate;
  const hasFilters = selectedCategories.length > 0 || hasDateFilter;

  // Get threshold from settings
  const threshold = settings?.high_value_threshold || 9000;

  // Filter transactions locally
  const transactions = useMemo(() => {
    return allTransactions.filter(trans => {
      // High value threshold filter
      if (trans.amount < threshold) return false;
      
      // Category filter
      if (selectedCategories.length > 0 && !selectedCategories.includes(trans.category)) return false;
      
      // Date range filter - only apply when BOTH dates are specified
      if (hasDateFilter) {
        try {
          const transDate = parse(trans.date, 'dd/MM/yyyy', new Date());
          if (transDate < startDate || transDate > endDate) return false;
        } catch (e) {
          return false;
        }
      }
      
      return true;
    });
  }, [allTransactions, selectedCategories, threshold, hasDateFilter, startDate, endDate]);

  // Pie data - show selected categories + "Others" when categories are selected
  const pieData = useMemo(() => {
    // First calculate totals from ALL high-value transactions (for "Others" calculation)
    const allHighValue = allTransactions.filter(t => t.amount >= threshold);
    
    // Apply date filter to all
    const dateFiltered = allHighValue.filter(trans => {
      if (startDate || endDate) {
        try {
          const transDate = parse(trans.date, 'dd/MM/yyyy', new Date());
          if (startDate && transDate < startDate) return false;
          if (endDate && transDate > endDate) return false;
        } catch (e) {
          return false;
        }
      }
      return true;
    });
    
    const categoryTotals = {};
    dateFiltered.forEach(trans => {
      const cat = trans.category || 'ETC';
      categoryTotals[cat] = (categoryTotals[cat] || 0) + trans.amount;
    });
    
    const allData = Object.entries(categoryTotals)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
    
    // If no categories selected, show all
    if (selectedCategories.length === 0) {
      return allData;
    }
    
    // Calculate selected categories and others
    let othersTotal = 0;
    const selectedData = [];
    
    allData.forEach(item => {
      if (selectedCategories.includes(item.name)) {
        selectedData.push(item);
      } else {
        othersTotal += item.value;
      }
    });
    
    // Add "Others" if there are unselected categories
    if (othersTotal > 0) {
      selectedData.push({ name: 'Others', value: othersTotal });
    }
    
    return selectedData;
  }, [allTransactions, selectedCategories, threshold, startDate, endDate]);

  const generatePDF = async () => {
    const doc = new jsPDF('landscape', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;

    // Title
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('TRACK CC - High Value Transactions', pageWidth / 2, 15, { align: 'center' });

    // Filters and summary
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    const filterParts = [];
    if (selectedCategories.length > 0) filterParts.push('Categories: ' + selectedCategories.join(', '));
    if (startDate && endDate) filterParts.push(`${format(startDate, 'dd/MM/yy')} - ${format(endDate, 'dd/MM/yy')}`);
    filterParts.push('Threshold: ' + threshold.toLocaleString('en-IN'));
    doc.text('Filters: ' + filterParts.join(' | '), pageWidth / 2, 22, { align: 'center' });

    const total = transactions.reduce((sum, t) => sum + t.amount, 0);
    doc.setFillColor(245, 245, 245);
    doc.rect(10, 28, pageWidth - 20, 12, 'F');
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Total Transactions: ' + transactions.length, 20, 35);
    doc.text('Total Amount: ' + total.toLocaleString('en-IN'), pageWidth / 2, 35);

    try {
      const chartY = 45;
      
      // Helper function to draw legend manually with small circles
      const drawLegend = (startX, startY, data, colors, maxWidth) => {
        doc.setFontSize(7);
        doc.setFont('helvetica', 'normal');
        const circleRadius = 1.5;
        const lineHeight = 4;
        const colWidth = maxWidth / 2;
        
        data.forEach((item, index) => {
          const col = index % 2;
          const row = Math.floor(index / 2);
          const x = startX + (col * colWidth);
          const y = startY + (row * lineHeight);
          
          // Draw small circle
          const color = colors[index % colors.length];
          const r = parseInt(color.slice(1, 3), 16);
          const g = parseInt(color.slice(3, 5), 16);
          const b = parseInt(color.slice(5, 7), 16);
          doc.setFillColor(r, g, b);
          doc.circle(x + circleRadius, y - 1, circleRadius, 'F');
          
          // Draw text aligned with circle
          doc.setTextColor(0, 0, 0);
          const name = item.name.length > 10 ? item.name.substring(0, 10) + '..' : item.name;
          doc.text(name, x + circleRadius * 2 + 2, y);
          doc.text(item.value.toLocaleString('en-IN', {maximumFractionDigits: 0}), x + colWidth - 3, y, { align: 'right' });
        });
        
        return Math.ceil(data.length / 2) * lineHeight;
      };
      
      // Capture and place Pie Chart SVG on left side (narrower)
      const pieChartSvg = document.querySelector('[data-testid="high-value-pie-chart"] .recharts-wrapper');
      if (pieChartSvg && transactions.length > 0) {
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text('By Category', 55, chartY, { align: 'center' });
        
        const canvas = await html2canvas(pieChartSvg, { scale: 2, backgroundColor: '#ffffff' });
        const imgData = canvas.toDataURL('image/png');
        const chartWidth = 90;
        const svgHeight = Math.min(pageHeight - chartY - 45, (canvas.height * chartWidth) / canvas.width);
        doc.addImage(imgData, 'PNG', 10, chartY + 5, chartWidth, svgHeight);
        
        // Draw legend manually below chart
        drawLegend(12, chartY + svgHeight + 10, pieData, CHART_COLORS, chartWidth - 4);
      }

      // Transaction list in TWO COLUMNS on right side
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('Transaction Details', 195, chartY, { align: 'center' });
      
      doc.setFontSize(7);
      doc.setFont('helvetica', 'normal');
      
      const col1X = 110;
      const col2X = 195;
      const colWidth = 80;
      const startY = chartY + 8;
      const lineHeight = 4;
      const maxYFirstPage = pageHeight - 15;
      
      let transIdx = 0;
      let yPosCol1 = startY;
      let yPosCol2 = startY;
      let useCol1 = true;
      
      // Fill both columns on first page
      while (transIdx < transactions.length) {
        const trans = transactions[transIdx];
        const transText = (transIdx + 1) + '. ' + trans.description.substring(0, 30);
        const detailText = trans.date + ' | ' + trans.amount.toLocaleString('en-IN') + ' | ' + trans.category;
        
        if (useCol1 && yPosCol1 + lineHeight * 2 < maxYFirstPage) {
          doc.text(transText, col1X, yPosCol1);
          yPosCol1 += lineHeight;
          doc.text('   ' + detailText, col1X, yPosCol1);
          yPosCol1 += lineHeight + 1;
          transIdx++;
          useCol1 = false;
        } else if (!useCol1 && yPosCol2 + lineHeight * 2 < maxYFirstPage) {
          doc.text(transText, col2X, yPosCol2);
          yPosCol2 += lineHeight;
          doc.text('   ' + detailText, col2X, yPosCol2);
          yPosCol2 += lineHeight + 1;
          transIdx++;
          useCol1 = true;
        } else if (yPosCol1 + lineHeight * 2 >= maxYFirstPage && yPosCol2 + lineHeight * 2 >= maxYFirstPage) {
          // Both columns full, break to next page
          break;
        } else {
          useCol1 = !useCol1;
        }
      }

      // Continue on next pages if needed (two columns full page)
      while (transIdx < transactions.length) {
        doc.addPage('landscape');
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text('Transaction Details (continued)', 20, 15);
        
        doc.setFontSize(7);
        doc.setFont('helvetica', 'normal');
        
        const fullCol1X = 20;
        const fullCol2X = 155;
        yPosCol1 = 25;
        yPosCol2 = 25;
        useCol1 = true;
        
        while (transIdx < transactions.length) {
          const trans = transactions[transIdx];
          const transText = (transIdx + 1) + '. ' + trans.description.substring(0, 45);
          const detailText = trans.date + ' | ' + trans.amount.toLocaleString('en-IN') + ' | ' + trans.category;
          
          if (useCol1 && yPosCol1 + lineHeight * 2 < pageHeight - 15) {
            doc.text(transText, fullCol1X, yPosCol1);
            yPosCol1 += lineHeight;
            doc.text('   ' + detailText, fullCol1X, yPosCol1);
            yPosCol1 += lineHeight + 1;
            transIdx++;
            useCol1 = false;
          } else if (!useCol1 && yPosCol2 + lineHeight * 2 < pageHeight - 15) {
            doc.text(transText, fullCol2X, yPosCol2);
            yPosCol2 += lineHeight;
            doc.text('   ' + detailText, fullCol2X, yPosCol2);
            yPosCol2 += lineHeight + 1;
            transIdx++;
            useCol1 = true;
          } else if (yPosCol1 + lineHeight * 2 >= pageHeight - 15 && yPosCol2 + lineHeight * 2 >= pageHeight - 15) {
            break;
          } else {
            useCol1 = !useCol1;
          }
        }
      }

      // Footer on all pages
      const pageCount = doc.internal.pages.length - 1;
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(128, 128, 128);
        doc.text('Generated by TRACK CC', pageWidth / 2, pageHeight - 5, { align: 'center' });
      }

      // Use blob URL method for better compatibility with preview environments
      const pdfBlob = doc.output('blob');
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'high-value-transactions.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success('PDF downloaded');
    } catch (e) {
      console.error('PDF error:', e);
      toast.error('Failed to generate PDF');
    }
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <div className="animate-pulse space-y-4">
          <div className="h-32 bg-muted rounded-xl"></div>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="p-6 space-y-6"
      data-testid="high-value-page"
    >
      <div className="space-y-2">
        <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight">High Value</h1>
        <p className="text-sm text-muted-foreground uppercase tracking-wider">
          Transactions Above {settings?.high_value_threshold?.toLocaleString('en-IN')}
        </p>
      </div>

      <Card data-testid="filters-card">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2 text-base">
              <Funnel size={18} weight="bold" />
              Filters
            </span>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={clearFilters} 
              className="h-8 px-2"
              data-testid="reset-all-filters-btn"
            >
              <ArrowCounterClockwise size={16} className="mr-1" />
              Reset All
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-2">
            <Label className="text-xs">Categories (select multiple)</Label>
            <Popover open={showCategoryPicker} onOpenChange={setShowCategoryPicker}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full justify-start text-left font-normal h-10"
                  data-testid="category-filter"
                >
                  <Tag size={16} className="mr-2" />
                  {selectedCategories.length === 0 
                    ? 'All Categories' 
                    : `${selectedCategories.length} selected`}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-56 p-2" align="start">
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {categories.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => toggleCategory(cat.name)}
                      className="flex items-center gap-2 w-full px-2 py-1.5 text-sm rounded hover:bg-muted transition-colors"
                    >
                      <div className={`w-4 h-4 rounded border flex items-center justify-center ${selectedCategories.includes(cat.name) ? 'bg-primary border-primary' : 'border-muted-foreground'}`}>
                        {selectedCategories.includes(cat.name) && <Check size={12} className="text-white" />}
                      </div>
                      {cat.name}
                    </button>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-xs">From Date</Label>
              <Popover open={showStartPicker} onOpenChange={setShowStartPicker}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left font-normal h-10"
                    data-testid="start-date-picker"
                  >
                    <CalendarBlank size={16} className="mr-2" />
                    {startDate ? format(startDate, 'dd/MM/yy') : 'Start'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={startDate}
                    onSelect={(date) => {
                      setStartDate(date);
                      setShowStartPicker(false);
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label className="text-xs">To Date</Label>
              <Popover open={showEndPicker} onOpenChange={setShowEndPicker}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left font-normal h-10"
                    data-testid="end-date-picker"
                  >
                    <CalendarBlank size={16} className="mr-2" />
                    {endDate ? format(endDate, 'dd/MM/yy') : 'End'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={endDate}
                    onSelect={(date) => {
                      setEndDate(date);
                      setShowEndPicker(false);
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs uppercase text-muted-foreground tracking-wider">Total Amount</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-lg font-bold tabular-nums text-[#800000]">
              {transactions.reduce((sum, t) => sum + t.amount, 0).toLocaleString('en-IN', {maximumFractionDigits: 0})}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs uppercase text-muted-foreground tracking-wider">Transactions</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-lg font-bold tabular-nums text-[#800000]">{transactions.length}</p>
          </CardContent>
        </Card>
      </div>

      {pieData.length > 0 && transactions.length > 0 && (
        <Card data-testid="high-value-pie-chart">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2">
              <ChartPie size={20} weight="bold" />
              By Category
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" labelLine={false} innerRadius={50} outerRadius={90} fill="#8884d8" dataKey="value">
                  {pieData.map((entry, index) => (
                    <Cell key={'cell-' + index} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => '' + value.toLocaleString('en-IN')} />
              </PieChart>
            </ResponsiveContainer>
            <div className="grid grid-cols-2 gap-1" data-testid="high-value-category-legend">
              {pieData.map((item, index) => (
                <div key={index} className="flex items-center gap-2">
                  <div
                    className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                    style={{ backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }}
                  />
                  <span className="text-xs font-medium truncate">{item.name}</span>
                  <span className="text-xs font-bold tabular-nums ml-auto flex-shrink-0">{item.value.toLocaleString('en-IN', {maximumFractionDigits: 0})}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {transactions.length > 0 && (
        <Button onClick={generatePDF} className="w-full rounded-full" data-testid="download-pdf-button">
          <DownloadSimple size={20} weight="bold" className="mr-2" />
          Download PDF Report
        </Button>
      )}

      <div className="space-y-3">
        {transactions.length === 0 ? (
          <Card className="text-center p-12" data-testid="no-transactions">
            <Wallet size={48} className="mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No high value transactions found</p>
          </Card>
        ) : (
          transactions.map((trans) => (
            <Card key={trans.id} data-testid="transaction-card">
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold mb-1 truncate">{trans.description}</p>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
                      <span>{trans.date}</span>
                      <span>-</span>
                      <span className="truncate max-w-[120px]">{trans.statement_name}</span>
                      <span>-</span>
                      <span className="font-medium text-foreground">{trans.category}</span>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-lg font-bold tabular-nums">{trans.amount.toLocaleString('en-IN', {maximumFractionDigits: 0})}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </motion.div>
  );
};

export default HighValue;
